import { Cancel, Delete } from "@mui/icons-material";
import { alpha, Button, Stack, styled } from "@mui/material";
import { AnimatePresence, motion } from "motion/react";
import { useState } from "react";

interface PopupCloseButtonProps {
    dismissText?: string;
    neverShowAgainText?: string;
    onDismiss: () => void;
    onNeverShowAgain: () => void;
}

export default function PopupCloseButton({
    dismissText = 'Dismiss',
    neverShowAgainText = 'Never show again',
    onDismiss,
    onNeverShowAgain
}: PopupCloseButtonProps) {

    const [isHovered, setIsHovered] = useState(false);

    const onMouseEnter = () => {
        setIsHovered(true);
    }

    const onMouseLeave = () => {
        setIsHovered(false);
    }

    const IconVariants = {
        hidden: {
            opacity: 0,
        },
        visible: {
            opacity: 1,
        },
    }

    const ButtonVariants = {
        hidden: {
            opacity: 0,
            y: '-100%',
        },
        visible: {
            opacity: 1,
            y: 0,
        },
    }

    return (
        <Root onMouseEnter={onMouseEnter} onMouseLeave={onMouseLeave}>
            <Background
                initial={{ opacity: 0 }}
                animate={{ opacity: isHovered ? 1 : 0 }}
                transition={{ duration: 0.2, ease: 'easeOut' }}
            />
            <AnimatePresence>
                <CancelIcon
                    key="cancel-icon"
                    variants={IconVariants}
                    initial={'visible'}
                    animate={isHovered ? 'hidden' : 'visible'}
                    exit={'visible'}
                />

                <FixedHeightStack
                    key="buttons"
                    direction="row"
                    spacing={2}
                    justifyContent="center"
                    justifyItems="center"
                    justifySelf="center"
                >
                    <MotionButton
                        key="never-show-again"
                        variants={ButtonVariants}
                        initial={'hidden'}
                        animate={isHovered ? 'visible' : 'hidden'}
                        transition={{ ease: 'easeIn' }}
                        exit={'hidden'}
                        variant="contained"
                        color="error"
                        size="small"
                        onClick={onNeverShowAgain}
                        startIcon={<Delete/>}
                    >
                        {neverShowAgainText}
                    </MotionButton>
                    <MotionButton
                        key="dismiss"
                        variants={ButtonVariants}
                        initial={'hidden'}
                        animate={isHovered ? 'visible' : 'hidden'}
                        transition={{ ease: 'easeIn' }}
                        exit={'hidden'}
                        variant="contained"
                        onClick={onDismiss}
                        color="primary"
                        size="small"
                        startIcon={<Cancel/>}
                        style={{
                            borderTopRightRadius: '10px'
                        }}
                    >
                        {dismissText}
                    </MotionButton>
                </FixedHeightStack>
            </AnimatePresence>
        </Root>
    )
}

const Root = styled('div')(({ theme }) => ({
    position: 'absolute',
    overflow: 'hidden',
    top: theme.spacing(1),
    right: theme.spacing(1),
    height: theme.spacing(6),
    padding: theme.spacing(.5),
    boxSizing: 'border-box',
    borderRadius: theme.spacing(1),
    color: theme.palette.primary.main,
    justifyContent: 'center',
    justifyItems: 'center',
    alignContent: 'center',
    alignItems: 'center',
}));

const Background = styled(motion.div)(({ theme }) => ({
    position: 'absolute',
    inset: 0,
    pointerEvents: 'none',
    background: `linear-gradient(
        to bottom,
        ${alpha(theme.palette.background.paper, .92)} 0,
        ${alpha(theme.palette.background.paper, .92)} ${theme.spacing(4.5)},
        ${alpha(theme.palette.background.paper, 0)} 100%
    )`,
}));

const CancelIcon = styled(motion.create(Cancel))(() => ({
    position: 'absolute',
    right: 0,
    width: 32,
    height: 32,
    zIndex: 1,
}))

const FixedHeightStack = styled(Stack)(() => ({
    height: '30px',
    minHeight: '30px',
    alignItems: 'center',
    position: 'relative',
    zIndex: 1,
}));

const MotionButton = styled(motion.create(Button))(() => ({
    height: '100%',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: '',
}));
